/**
 * Spec D §7.2.2 tier vocabulary + chip label derivation. Node-safe (no React/
 * MUI) so builder-service unit tests can lock the copy; StatTierChip renders
 * from here. Labels are pure formatting over SERVER-computed qualification —
 * no statistics happen on the client (AC-D1-7).
 */

import type { MetricQualification, StatTier } from './types';

/** §7.2.2 copy table — reuse this wording everywhere tiers are explained. */
export const STAT_TIER_COPY: Record<StatTier, { icon: string; meaning: string }> = {
  solid: { icon: '✓', meaning: 'trustworthy' },
  directional: {
    icon: '~',
    meaning: 'direction is reliable, size is not — act, then verify with an A/B',
  },
  insufficient: { icon: '✗', meaning: 'too early to judge' },
};

export function formatTierChipLabel(qualification: MetricQualification): string {
  const { tier, p, ciLow, ciHigh, srmOk, neededN } = qualification;
  const icon = STAT_TIER_COPY[tier].icon;
  if (srmOk === false) return `${icon} not comparable`;
  if (tier === 'solid') {
    const halfWidthPp = ((ciHigh - ciLow) / 2) * 100;
    return `${icon} ${formatPct(p)} ± ${formatPp(halfWidthPp)}`;
  }
  if (tier === 'directional') return `${icon} direction only`;
  if (neededN && neededN > 0) return `${icon} needs ~${neededN} more`;
  return `${icon} too early`;
}

/**
 * The same verdict WITHOUT the rate. The Reports gates band renders the rate
 * itself 4 px above the chip, so the full label repeated it — the very defect
 * D5 removed from the versions table («it repeated the rate and its interval a
 * third time»). The interval stays one click deep, in the chip's own popover.
 */
export function formatTierChipVerdict(qualification: MetricQualification): string {
  const { tier, srmOk, neededN } = qualification;
  const icon = STAT_TIER_COPY[tier].icon;
  if (srmOk === false) return `${icon} not comparable`;
  if (tier === 'solid') return `${icon} solid`;
  if (tier === 'directional') return `${icon} direction only`;
  if (neededN && neededN > 0) return `${icon} needs ~${neededN} more`;
  return `${icon} too early`;
}

export function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatPp(value: number): string {
  return value >= 10 ? value.toFixed(0) : value.toFixed(1);
}
